import ArchiveRounded from '@mui/icons-material/ArchiveRounded';
import BadgeIcon from '@mui/icons-material/Badge';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteForeverRounded from '@mui/icons-material/DeleteForeverRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import DirectionsWalkRounded from '@mui/icons-material/DirectionsWalkRounded';
import DoorFrontRounded from '@mui/icons-material/DoorFrontRounded';
import DraftsRounded from '@mui/icons-material/DraftsRounded';
import DrawRounded from '@mui/icons-material/DrawRounded';
import ForwardRounded from '@mui/icons-material/ForwardRounded';
import MarkunreadMailboxRounded from '@mui/icons-material/MarkunreadMailboxRounded';
import PersonPinRounded from '@mui/icons-material/PersonPinRounded';
import QrCodeScanner from '@mui/icons-material/QrCodeScanner';
import SendRounded from '@mui/icons-material/SendRounded';
import SvgIcon from '@mui/material/SvgIcon';
import { FunctionComponent } from 'react';

import { Piece, PieceAction } from '@apiContract';
import FinishPickUpWithIDCard from '@components/MailpieceActions/components/actions/FinishPickUpWithIDCard';
import { PaletteColorKey } from '@minimalsTheme/core/palette';

import { RecycleIcon } from '@src/assets/icons/RecycleIcon';
import { ReturnToSender } from '@src/assets/icons/ReturnToSender';

import { CombinedActionsDrawer } from './components/CombinedActionsDrawer';
import { Archive } from './components/actions/Archive';
import { Delete } from './components/actions/Delete';
import { FinishDelivery } from './components/actions/FinishDelivery';
import FinishDeliveryWithImage from './components/actions/FinishDeliveryWithImage/index';
import { FinishDeliveryWithQrCode } from './components/actions/FinishDeliveryWithQrCode';
import { FinishDeliveryWithSignature } from './components/actions/FinishDeliveryWithSignature';
import { FinishMail } from './components/actions/FinishMail';
import { FinishPickUp } from './components/actions/FinishPickUp';
import { FinishPickUpWithQrCode } from './components/actions/FinishPickUpWithQrCode';
import { FinishPickUpWithSignature } from './components/actions/FinishPickUpWithSignature';
import { FinishRecycle } from './components/actions/FinishRecycle';
import { FinishReturnToSender } from './components/actions/FinishReturnToSender';
import { FinishShred } from './components/actions/FinishShred';
import { Forward } from './components/actions/Forward';
import { MarkPickedUp } from './components/actions/MarkPickedUp';
import { OpenAndScan, mailpieceHasContent } from './components/actions/OpenAndScan';
import { RequestDelivery } from './components/actions/RequestDelivery';
import { RequestMailToMe } from './components/actions/RequestMailToMe';
import { RequestOpenAndScan } from './components/actions/RequestOpenAndScan';
import { RequestPickUp } from './components/actions/RequestPickUp';
import { RequestRecycle } from './components/actions/RequestRecycle';
import { RequestReturnToSender } from './components/actions/RequestReturnToSender';
import { RequestShred } from './components/actions/RequestShred';
import { Submit } from './components/actions/Submit';
import { CombinedActions, CommonAction, ValidationData } from './types';

export { MailpieceActionButton } from './components/MailpieceActionButton';
export { MailpieceActionsMenu } from './components/MailpieceActionsMenu';
export { MailpieceBulkActions } from './components/MailpieceBulkActions';
export { MailpieceActionLabel } from './components/MailpieceActionLabel';

type SvgIconComponent = typeof SvgIcon;

export type ActionComponentProps = {
  mailpieces: Piece[];
  actionStarted: boolean;
  onActionFinish: () => void;
  onClose?: () => void;
  type?: string;
  pendingSearchValue?: string;
  isForce?: boolean;
  validationData?: ValidationData;
};

// TODO: Refactoring idea: 'mailpieceActions' is becoming a hook.
// 'validationSettingName' is becoming boolean 'requiresValidation'.
// 'availabilitySettingName' should be moved from here to \src\pages\MailroomPendingRequests\hooks\useRequestedActions.ts.
export type MailpieceAction = {
  title: string | ((mailpiece?: Piece) => string);
  Icon: SvgIconComponent;
  color?: string;
  actionTriggerColor?: PaletteColorKey;
  Component?: FunctionComponent<ActionComponentProps>;
  validationSettingName?: string | ((mailpiece: Piece) => string | undefined);
  availabilitySettingName?: string[];
  group?: CombinedActions;
};

type MailpieceActions = {
  [key in CommonAction]?: MailpieceAction;
};

export const mailpieceActions: MailpieceActions = {
  [PieceAction.Delete]: {
    title: 'Delete',
    Icon: DeleteRounded,
    actionTriggerColor: 'error',
    Component: Delete,
  },

  [PieceAction.Assign]: {
    title: 'Assign',
    Icon: PersonPinRounded,
  },

  [PieceAction.Submit]: {
    title: 'Send',
    Icon: SendRounded,
    Component: Submit,
  },

  [PieceAction.RequestForward]: {
    title: 'Forward',
    Icon: ForwardRounded,
    Component: Forward,
    availabilitySettingName: ['MailRequestForwardEnable', 'PackageRequestForwardEnable'],
  },

  [PieceAction.RequestRecycle]: {
    title: 'Recycle',
    Icon: RecycleIcon,
    color: 'error.main',
    Component: RequestRecycle,
    availabilitySettingName: ['MailRequestRecycleEnable'],
  },

  [PieceAction.RequestOpenAndScan]: {
    title: 'Open & Scan',
    Icon: DraftsRounded,
    color: 'success.main',
    Component: RequestOpenAndScan,
    availabilitySettingName: ['MailRequestOpenAndScanEnable', 'PackageRequestOpenAndScanEnable'],
  },

  [PieceAction.RequestMail]: {
    title: 'Mail',
    color: 'info.main',
    Icon: MarkunreadMailboxRounded,
    Component: RequestMailToMe,
    availabilitySettingName: ['MailRequestMailEnable', 'PackageRequestMailEnable'],
  },

  [PieceAction.RequestPickUp]: {
    title: 'Pick Up',
    color: 'warning.main',
    Icon: DirectionsWalkRounded,
    Component: RequestPickUp,
    availabilitySettingName: ['MailRequestPickUpEnable', 'PackageRequestPickUpEnable'],
  },

  [PieceAction.RequestShred]: {
    title: 'Shred',
    Icon: DeleteForeverRounded,
    color: 'orange.main',
    Component: RequestShred,
    availabilitySettingName: ['MailRequestShredEnable'],
  },

  [PieceAction.RequestDelivery]: {
    title: 'Deliver',
    Icon: DoorFrontRounded,
    color: 'purple.main',
    Component: RequestDelivery,
    availabilitySettingName: ['MailRequestDeliveryEnable', 'PackageRequestDeliveryEnable'],
  },

  [PieceAction.RequestReturnToSender]: {
    title: 'Return to Sender',
    Icon: ReturnToSender,
    color: 'brown.main',
    Component: RequestReturnToSender,
    availabilitySettingName: ['MailRequestReturnToSenderEnable', 'PackageRequestReturnToSenderEnable'],
  },

  [PieceAction.FinishRecycle]: {
    title: 'Recycle',
    Icon: RecycleIcon,
    Component: FinishRecycle,
  },

  [PieceAction.FinishOpenAndScan]: {
    title: (mailpiece) => {
      if (!mailpiece || !mailpieceHasContent(mailpiece)) return 'Open & Scan';
      return mailpiece.pieceDoc?.pdfUrl ? 'Send Contents' : 'Convert & Send Contents';
    },
    Icon: DraftsRounded,
    Component: OpenAndScan,
    validationSettingName: (mailpiece) =>
      !mailpieceHasContent(mailpiece) ? 'PackageValidationOpenAndScan' : undefined,
  },

  [PieceAction.FinishMail]: {
    title: 'Mail',
    Icon: MarkunreadMailboxRounded,
    Component: FinishMail,
    validationSettingName: 'PackageValidationMail',
  },

  [PieceAction.FinishPickUp]: {
    title: 'Pick Up',
    Icon: DirectionsWalkRounded,
    Component: FinishPickUp,
    validationSettingName: 'PackageValidationPickup',
  },

  [PieceAction.FinishShred]: {
    title: 'Shred',
    Icon: DeleteForeverRounded,
    Component: FinishShred,
  },

  [PieceAction.FinishPickUpWithSignature]: {
    title: 'Pick Up with Signature',
    Icon: DirectionsWalkRounded,
    Component: FinishPickUpWithSignature,
    group: CombinedActions.CombinedPickUp,
  },

  [PieceAction.FinishPickupMailbox]: {
    title: 'Mark as Picked Up',
    Icon: ArchiveRounded,
    Component: MarkPickedUp,
  },

  [PieceAction.FinishPickUpWithQrCode]: {
    title: 'Pick Up with QR Code',
    Icon: QrCodeScanner,
    Component: FinishPickUpWithQrCode,
    group: CombinedActions.CombinedPickUp,
  },

  [PieceAction.FinishPickupWithIdCard]: {
    title: 'Pick Up with ID Card',
    Icon: BadgeIcon,
    Component: FinishPickUpWithIDCard,
    group: CombinedActions.CombinedPickUp,
  },

  [PieceAction.FinishDelivery]: {
    title: 'Deliver',
    Icon: DoorFrontRounded,
    Component: FinishDelivery,
  },

  [PieceAction.FinishDeliveryWithSignature]: {
    title: 'Deliver with Signature',
    Icon: DrawRounded,
    Component: FinishDeliveryWithSignature,
    group: CombinedActions.CombinedDelivery,
  },

  [PieceAction.FinishDeliveryWithQrCode]: {
    title: 'Deliver with QR Code',
    Icon: QrCodeScanner,
    Component: FinishDeliveryWithQrCode,
    group: CombinedActions.CombinedDelivery,
  },

  [PieceAction.FinishDeliveryWithImage]: {
    title: 'Deliver with Image',
    Icon: CameraAltIcon,
    Component: (props) => <FinishDeliveryWithImage {...props} type="delivery" />,
    group: CombinedActions.CombinedDelivery,
  },

  [PieceAction.FinishPickupWithImage]: {
    title: 'Pick Up with Image',
    Icon: CameraAltIcon,
    Component: (props) => <FinishDeliveryWithImage {...props} type="pickup" />,
    group: CombinedActions.CombinedPickUp,
  },

  [PieceAction.FinishReturnToSender]: {
    title: 'Return to Sender',
    Icon: ReturnToSender,
    Component: FinishReturnToSender,
    validationSettingName: 'PackageValidationReturnToSender',
  },

  [PieceAction.Archive]: {
    title: 'Archive',
    Icon: ArchiveRounded,
    Component: Archive,
  },

  [CombinedActions.CombinedPickUp]: {
    title: 'Pick Up',
    Icon: DirectionsWalkRounded,
    validationSettingName: 'PackageValidationPickup',
    Component: (props) => (
      <CombinedActionsDrawer
        {...props}
        title="Select Completion Method"
        actions={[
          PieceAction.FinishPickUpWithSignature,
          PieceAction.FinishPickUpWithQrCode,
          PieceAction.FinishPickupWithImage,
          PieceAction.FinishPickupWithIdCard,
        ]}
      />
    ),
  },

  [CombinedActions.CombinedDelivery]: {
    title: 'Deliver',
    Icon: DoorFrontRounded,
    validationSettingName: 'PackageValidationDeliver',
    Component: (props) => (
      <CombinedActionsDrawer
        {...props}
        title="Select Completion Method"
        actions={[
          PieceAction.FinishDeliveryWithSignature,
          PieceAction.FinishDeliveryWithQrCode,
          PieceAction.FinishDeliveryWithImage,
        ]}
      />
    ),
  },
};
